import React, { useCallback, useEffect } from "react";
import styled from "styled-components";
import { BoxVideo, Item } from "../utils/boxTypes";
import { formatCurrency, formatCurrencyWithoutDecimal, formatNumber, getImageSource, getVideoPath } from "../utils/utility";
import MediaDisplayForWinningItem from "./MediaDisplayForWinningItem";
import useConfirmation from "./confirm_modal";
import { AppDispatch, RootState } from "../app/store";
import { useDispatch, useSelector } from "react-redux";
import { rejectWinningItem, setWinningItems } from "../features/winningSlice";
import { getVpcPrice } from "../features/walletSlice";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7); /* Dark semi-transparent background */
  z-index: 999; /* Ensure it appears behind the dialog */
`;

const DialogueWrapper = styled.div<{ itemCount: number }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #222;
  color: #fff;
  border-radius: 12px;
  width: ${({ itemCount }) =>
    itemCount == 1 ? "250px" : `calc(min(${itemCount * 150 + (itemCount - 1) * 20}px, 90%))`}; /* Dynamic width */
  max-width: 90%;
  padding: 20px 15px;
  z-index: 1000; /* Higher than the overlay */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;

  &:hover {
    color: #ff0077;
  }
`;


const ActionButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  cursor: pointer;
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 142px;
  height: 40px;
  border-radius: 8px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.25);
  font-family: 'Corbel', sans-serif; /* Use Corbel font */
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #fff;
  background-color: #00bbcc;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease;

  // Default shadow effect
  box-shadow: inset 4px 8px 12px 0 rgba(255, 255, 255, 0.5),
    inset -2px -6px 10px 0 rgba(0, 0, 0, 0.6),
    0px 5px 10px rgba(0, 0, 0, 0.2);

  // Hover effect: slight scale up and darker background
  &:hover {
    background-color: #00a0b0;
    transform: scale(1.05); // Slight scale up on hover
    box-shadow: 0px 10px 15px rgba(0, 0, 0, 0.3); // More pronounced shadow on hover
  }

  // Active (press) effect: scale down and change shadow
  &:active {
    background-color: #008c9e;
    transform: scale(0.95); // Scale down when clicked
    box-shadow: inset 4px 6px 10px rgba(0, 0, 0, 0.8), 0px 4px 8px rgba(0, 0, 0, 0.3); // Stronger shadow inset
  }

  // Disabled state: gray background, no hover or click effect
  &:disabled {
    background-color: #b0b0b0;
    cursor: not-allowed;
    box-shadow: none;
    color: #888;
    transform: none; // No scale effects
  }

  @media (max-width: 600px) {
    margin-top: 8px;
  }
`;



const ItemsGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  gap: 16px;
  margin-top: 16px;
`;

const ItemContainer = styled.div`
  width: 100%;
  padding-top: 157.14%;
  position: relative;
  border-radius: 16px;
  font-family: SonsieOne;
  font-size: 24px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  background-image: url("/assets/Item_Container_BG.png"); /* Add your image path here */
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  overflow: hidden; /* Ensure content doesn't affect the box size */
  border-radius: 16px;
  background-image: linear-gradient(160deg, #407a80 6%, rgba(0, 170, 255, 0.2) 100%), linear-gradient(to bottom, #407a80 0%, #407a80 100%);

`;

const ItemContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px; /* Adjust padding as needed */
  box-sizing: border-box;
`;

const ItemTitle = styled.h3`
  font-family: Verdana;
  font-size: 12px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #fff;
  position: absolute;
  /* bottom: auto; */
  top: 80.5%; /* Reset top property */
  left: 7.1%;
  right: 7.1%;
  margin: 0; /* Remove default margin */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemPrice = styled.div`
  width: auto;
  height: auto;
  margin: 20px 8px 5px 0;
  padding: 2px 10px 1px;
  background-color: #80d5ff;
  position: absolute;
  bottom: 22%;
  left: 7.1%;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  align-items: center; /* Vertical centering */
  font-family: Verdana;
  font-size: clamp(
    10px,
    3vw,
    14px
  ); /* Adjust the min, viewport width, and max values */
  /* font-size: 1vw; Adjust the multiplier as needed */
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: #fd3;
`;

const SellButton = styled.button`
  margin-top: 10px;
  padding: 10px 20px;
  background-color: #ff6f61; /* Elegant selling color */
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Corbel', sans-serif; /* Consistent font */
  font-size: 16px; /* Align with ActionButton */
  font-weight: bold;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease;

  // Default shadow effect
  box-shadow: inset 3px 6px 8px rgba(255, 255, 255, 0.5),
    inset -2px -5px 8px rgba(0, 0, 0, 0.5),
    0px 4px 8px rgba(0, 0, 0, 0.2);

  // Hover effect
  &:hover {
    background-color: #ff4c42; /* Slightly darker selling color */
    transform: scale(1.05); // Slight scale up
    box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.3); // Enhanced shadow
  }

  // Active (press) effect
  &:active {
    background-color: #e84338; /* Even darker selling color */
    transform: scale(0.95); // Scale down
    box-shadow: inset 3px 5px 8px rgba(0, 0, 0, 0.6), 0px 3px 6px rgba(0, 0, 0, 0.3);
  }

  // Disabled state
  &:disabled {
    background-color: #b0b0b0;
    color: #888;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  @media (max-width: 600px) {
    margin-top: 8px;
    padding: 8px 15px; /* Adjust for smaller screens */
  }
`;

const CreditMessage = styled.div`
  text-align: center;
  font-size: 1.2rem;
  color: #4CAF50;
  font-weight: bold;
  margin: 10px 0;
`;



const BoxWinningDialogue: React.FC<{
  winningItems: Item[] | null;
  boxVideos: BoxVideo[] | null;
  type: string;
  boxCurrencyType: string;
  boxContentType: string;
  onClose: () => void;
}> = ({ winningItems, boxVideos, type, boxCurrencyType, boxContentType, onClose }) => {
  const { confirm, ConfirmationModal } = useConfirmation();
  const dispatch: AppDispatch = useDispatch(); // Use AppDispatch

  const { winningItems: currentWinningItems, rejectWinningItemStatus } = useSelector(
    (state: RootState) => state.winningSice
  );

  const { vpcPrice } = useSelector((state: RootState) => state.wallet);


  useEffect(() => {
    if (winningItems) {
      dispatch(setWinningItems(winningItems)); // Set the winning items in Redux
    }
    dispatch(getVpcPrice()); // Fetch VPC price only for VPC boxes

  }, [dispatch, winningItems]);

  if (!winningItems) return null;


  const itemCount = winningItems.length;

  const getGridItem = (item: Item, index: number, boxVideos: BoxVideo[] | null) => {
    const videoFile = boxVideos?.find((video) => video.item_id === item.item_id)?.file || null;
    const imageSource = getImageSource(item.detail?.picture);
    const videoSource = videoFile ? getVideoPath(videoFile) : null;

    return (
      <ItemContainer
        key={index}
      >
        <ItemContent >
          <MediaDisplayForWinningItem
            selectedBox={{
              picture: imageSource,
              video: videoSource,
            }}
          />
          <ItemTitle>{item.detail?.name}</ItemTitle>
          <ItemPrice>{getPrice(item)}</ItemPrice>
        </ItemContent>
      </ItemContainer>
    );
  };

  const handleSell = async (item: Item, index: number, type: string) => {
    const imageSource = getImageSource(item.detail?.picture);
    try {
      const isConfirmed = await confirm(
        <>
          <p>
            You are about to sell <span className="highlight">{item.detail?.name}</span> for{" "}
            <span className="highlight">{getPrice(item)}</span>.
          </p>
        </>,
        imageSource,
        " "
      );

      if (isConfirmed) {
        // alert(`Selling ${item.detail?.name} for $${formatNumber(item.detail?.price)}, id = ${item.id}, item_id =${item.item_id}, type =${type}`);
        // Add your selling logic here
        dispatch(rejectWinningItem({ id: item.id!, itemId: item.item_id, type: type })); // Dispatch rejectWinningItem
      } else {
        console.log("Sell action was canceled.");
      }
    } catch (error) {
      console.error("An error occurred during the sell action:", error);
    }
  };

  const isButtonLoading = () => rejectWinningItemStatus === "loading";


  function getPrice(item: Item) {

    if (boxCurrencyType == 'vpc') {
      if (!vpcPrice?.usdt_vpc?.amount) {
        return "Loading..."; // Show a fallback if VPC price isn't available
      }
      return `${formatCurrencyWithoutDecimal(item.detail?.price! * vpcPrice.usdt_vpc.amount)} VPC`;
    }
    return `$${formatCurrency(item.detail?.price ?? 0)}`;
  }


  function getAllItemsPriceTotal(items: Item[]) {
    const totalAmount = currentWinningItems.reduce((sum, item) => sum + (item.detail?.price ?? 0), 0);

    if (boxCurrencyType == 'vpc') {
      if (!vpcPrice?.usdt_vpc?.amount) {
        return "Loading..."; // Show a fallback if VPC price isn't available
      }
      return `${formatCurrencyWithoutDecimal(totalAmount * vpcPrice.usdt_vpc.amount)} VPC`;
    }
    return `$${formatCurrency(totalAmount)}`;
  }



  return (
    <>
      <Overlay />
      <DialogueWrapper itemCount={itemCount}>
        <ConfirmationModal />

        <CloseButton onClick={onClose}>×</CloseButton>
        <h2 style={{ textAlign: "center", fontSize: "1.5rem", margin: "10px 0" }}>
          {currentWinningItems.length > 1 ? 'You won the following items' : 'You won the following item'}
        </h2>
        <ItemsGrid columns={itemCount}>
          {currentWinningItems.map((item, index) => getGridItem(item, index, boxVideos))}
        </ItemsGrid>
        {
          boxContentType === "credit" &&
          <CreditMessage >
            🎉 Congratulations! Your account has been credited with {getAllItemsPriceTotal(currentWinningItems)}
          </CreditMessage>

        }
        <ItemsGrid columns={itemCount}>
          {currentWinningItems.map((item, index) => (
            (boxContentType === "physical" || boxContentType === 'digital') && (
              <SellButton
                key={index}
                onClick={() => handleSell(item, index, type)}
                disabled={item.isRejected || isButtonLoading()}
              >
                {isButtonLoading()
                  ? "Loading..."
                  : item.isRejected
                    ? `Exchanged for ${getPrice(item)}`
                    : `Sell for ${getPrice(item)}`}
              </SellButton>
            )
          ))}
        </ItemsGrid>


        <ActionButton onClick={onClose}>Ok</ActionButton>
      </DialogueWrapper>
    </>
  );
};

export default BoxWinningDialogue;
